import { useState, useCallback, useRef } from 'react';
import { FiltrosProveedor, ProveedorListItem } from '../types';
import { proveedorAdminService } from '../services/proveedorAdminService';

const LIMITE_DEFAULT = 10;

export function useProveedoresAdmin() {
  const [proveedores, setProveedores] = useState<ProveedorListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filtros, setFiltros] = useState<FiltrosProveedor>({
    pagina: 1,
    limite: LIMITE_DEFAULT,
  });

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cargar = useCallback(async (f: FiltrosProveedor) => {
    try {
      setLoading(true);
      setError(null);
      const res = await proveedorAdminService.listar(f);
      setProveedores(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando proveedores');
    } finally {
      setLoading(false);
    }
  }, []);

  const aplicarFiltros = useCallback((nuevosFiltros: Partial<FiltrosProveedor>) => {
    const updated = { ...filtros, ...nuevosFiltros, pagina: 1 };
    setFiltros(updated);
    cargar(updated);
  }, [filtros, cargar]);

  const cambiarPagina = useCallback((pagina: number) => {
    const updated = { ...filtros, pagina };
    setFiltros(updated);
    cargar(updated);
  }, [filtros, cargar]);

  const buscar = useCallback((busqueda: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      aplicarFiltros({ busqueda: busqueda || undefined });
    }, 300);
  }, [aplicarFiltros]);

  const cancelBusqueda = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
  }, []);

  const recargar = useCallback(() => cargar(filtros), [cargar, filtros]);

  const eliminar = useCallback(async (id: number) => {
    await proveedorAdminService.eliminar(id);
    await recargar();
  }, [recargar]);

  return {
    proveedores,
    total,
    loading,
    error,
    filtros,
    aplicarFiltros,
    cambiarPagina,
    buscar,
    cancelBusqueda,
    recargar,
    eliminar,
    cargar,
  };
}
